import React, { useState, useRef, useCallback, useMemo, useEffect } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { Button, makeStyles } from "@material-ui/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { FieldModel } from "./types";
import { createFieldsPlugin } from "./createFieldsPlugin";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "@react-pdf-viewer/search/lib/styles/index.css";
import { pageNavigationPlugin } from "@react-pdf-viewer/page-navigation";

const PDFJS_VERSION = "2.16.105";

const useStyles = makeStyles((theme) => ({
	documentEditorContainer: {
		display: "flex",
		flexDirection: "column",
		gap: 16,
		height: "80vh",
		position: "relative",
	},
	viewerContainer: {
		flex: 1,
		position: "relative",
		overflow: "auto",
		"& .rpv-core__viewer": {
			height: "100%",
		},
		"& .rpv-core__page-layer": {
			position: "relative",
		},
	},
	buttonContainer: {
		display: "flex",
		justifyContent: "center",
		gap: 16,
		padding: "16px",
		backgroundColor: "white",
	},
	rejectButton: {
		backgroundColor: theme.palette.error.main,
		color: "white",
		"&:hover": {
			backgroundColor: theme.palette.error.dark,
		},
	},
	completeButton: {
		backgroundColor: theme.palette.success.main,
		color: "white",
		"&:hover": {
			backgroundColor: theme.palette.success.dark,
		},
	},
	fieldsContainer: {
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		pointerEvents: "none",
		"& > *": {
			pointerEvents: "auto",
		},
	},
}));

interface FieldValue {
	id: string;
	value: string;
	fieldType: string;
}

interface DocumentEditorProps {
	fileUrl: string;
	fields: FieldModel[];
	onReject?: () => void;
	onComplete?: () => void;
	onFieldChange?: (fieldId: string, value: string, fieldType?: string) => void;
	fieldValues?: FieldValue[];
	readOnly?: boolean;
	activeFieldId?: string | null;
	scrollToFieldRef?: React.MutableRefObject<((fieldId: string) => void) | null>;
}

const DocumentEditor: React.FC<DocumentEditorProps> = ({
	fileUrl,
	fields,
	onReject,
	onComplete,
	onFieldChange,
	fieldValues = [],
	activeFieldId,
	scrollToFieldRef,
}) => {
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [scale, setScale] = useState(1);
	const viewerContainerRef = useRef<HTMLDivElement>(null);
	const pageNavigationPluginInstance = pageNavigationPlugin();

	const classes = useStyles();

		const layout = defaultLayoutPlugin({
		sidebarTabs: (defaultTabs) => [],
		toolbarPlugin: {
			fullScreenPlugin: {
				onEnterFullScreen: (zoom) => {
					zoom(1.5);
					return Promise.resolve();
				},
			},
		},
	});

	const onSelect = useCallback((id: string) => {
		setSelectedId(id);
	}, []);

	const scrollToField = useCallback((fieldId: string) => {
		const field = fields.find(f => f.id === fieldId);
		if (!field || !field.fieldPosition) {
			console.warn(`Field ${fieldId} has no position data`);
			return;
		}

		const { page } = field.fieldPosition;
		const { jumpToPage } = pageNavigationPluginInstance;

		jumpToPage(page - 1);

		setTimeout(() => {
			const fieldElement = document.querySelector(`[data-field-id="${fieldId}"]`);
			if (fieldElement) {
				fieldElement.scrollIntoView({
					behavior: 'smooth',
					block: 'center',
					inline: 'center'
				});
			}
		}, 300);
	}, [fields, pageNavigationPluginInstance]);

	useEffect(() => {
		if (scrollToFieldRef) {
			scrollToFieldRef.current = scrollToField;
		}
		return () => {
			if (scrollToFieldRef) {
				scrollToFieldRef.current = null;
			}
		};
	}, [scrollToField, scrollToFieldRef]);

	const pluginInstance = useMemo(
		() => {
			console.log('DocumentEditor fieldValues changed:', fieldValues); // Debug log
			return createFieldsPlugin(fields, onSelect, selectedId, fieldValues, activeFieldId);
		},
		[fields, onSelect, selectedId, fieldValues, activeFieldId]
	);

	const handleFieldChange = useCallback((fieldId: string, value: string, fieldType?: string) => {
		if (onFieldChange) {
			onFieldChange(fieldId, value, fieldType);
		}
	}, [onFieldChange]);

	return (
		<div className={classes.documentEditorContainer}>
			<div
				className={classes.viewerContainer}
				ref={viewerContainerRef}
				onClick={(e) => {
					// Only clear selection if clicking on the background, not on fields
					if (e.target === e.currentTarget) {
						setSelectedId(null);
					}
				}}
			>
				<Worker
					workerUrl={`https://unpkg.com/pdfjs-dist@${PDFJS_VERSION}/legacy/build/pdf.worker.js`}
				>
					<Viewer
						defaultScale={1}
						fileUrl={fileUrl}
						plugins={[layout, pageNavigationPluginInstance, pluginInstance]}
					/>
				</Worker>
			</div>
		</div>
	);
};

export default DocumentEditor;
